import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '../ui';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

/**
 * Genera un blob JPEG del área recortada.
 * @param {string} imageSrc - Data URL o blob URL de la imagen
 * @param {{ x: number, y: number, width: number, height: number }} cropAreaPixels - Área en píxeles
 * @returns {Promise<Blob>}
 */
export async function getCroppedImg(imageSrc, cropAreaPixels) {
  const img = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const { x, y, width, height } = cropAreaPixels;

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Error al crear imagen recortada'));
    }, 'image/jpeg', 0.9);
  });
}

/**
 * Modal de recorte de imagen (avatar 1:1 o banner 3:1).
 * Props: imageSrc, aspect, onComplete(blob), onCancel
 */
const ImageCropModal = ({ imageSrc, aspect, onComplete, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setApplying(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onComplete(blob);
    } catch (err) {
      console.error('Error al recortar:', err);
    } finally {
      setApplying(false);
    }
  }, [croppedAreaPixels, imageSrc, onComplete]);

  const handleBackdropClick = useCallback(() => {
    if (!applying) onCancel();
  }, [applying, onCancel]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Recortar imagen"
    >
      <div
        className="flex flex-col w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-xl bg-gray-900 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-[380px] min-h-0 shrink-0">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            style={{
              containerStyle: { backgroundColor: '#111827' },
              cropAreaStyle: { border: '2px solid white' }
            }}
          />
        </div>
        <div className="flex items-center justify-between p-4 border-t border-gray-700 shrink-0">
          <div className="text-sm text-gray-400">
            {aspect === 1 ? 'Recorta tu foto de perfil (cuadrado)' : 'Recorta tu banner (ancho 3:1)'}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={applying}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="gradient"
              onClick={handleApply}
              loading={applying}
              disabled={applying || !croppedAreaPixels}
            >
              Usar esta zona
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
